import { useState, useRef } from "react";
import { Upload, X, Loader2, CheckCircle2, MessageCircle, Image as ImageIcon, FileVideo } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { whatsappLink } from "@/config/site";
import { track } from "@/lib/analytics";

const PROPERTY_TYPES = ["Studio", "Apartment", "House", "Office or other"];
const YESNO = ["Yes", "No", "Not sure"];
const PACKAGES = ["Move & Clean Package", "Move, Clean & Clear Package", "Full Move-Out Package", "Not sure yet"];

const inputCls = "h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20 focus-visible:border-emerald-600";

const Field = ({ label, required, children, testid }) => (
  <div className="flex flex-col gap-1.5" data-testid={testid}>
    <Label className="text-sm font-semibold text-slate-700">{label} {required && <span className="text-emerald-600">*</span>}</Label>
    {children}
  </div>
);

export const PackageQuoteForm = ({ defaultPackage = "" }) => {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", package: defaultPackage,
    collection_address: "", delivery_address: "", property_type: "", bedrooms: "",
    moving_date: "", cleaning_date: "", items_to_move: "", num_boxes: "", heavy_items: "",
    cleaning_required: "", rubbish_removal_required: "", furniture_assembly_required: "",
    message: "", company: "",
  });
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = (list) => {
    const arr = Array.from(list).filter((f) => f.size <= 60 * 1024 * 1024);
    if (arr.length !== list.length) toast.error("Some files exceed the 60MB limit and were skipped.");
    setFiles((prev) => [...prev, ...arr].slice(0, 10));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name";
    if (!/^[+\d][\d\s()-]{5,}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!consent) e.consent = "Please tick the consent box";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      await api.post("/packages", { ...form, uploaded_files: uploaded, consent, source_page: "/moving-cleaning-packages" });
      track.formSubmit("moving-cleaning-package");
      setDone(true);
      window.scrollTo({ top: document.getElementById("package-form")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try WhatsApp.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div id="package-form" className="rounded-3xl border border-emerald-200 bg-white p-10 text-center shadow-xl" data-testid="package-success">
        <CheckCircle2 size={52} className="mx-auto text-emerald-600" />
        <h3 className="mt-4 font-display text-2xl font-black text-slate-900">Thank you</h3>
        <p className="mt-2 text-slate-600">We've received your combined moving + cleaning request and will reply with one clear quote. For a faster response, send your photos or a short video on WhatsApp.</p>
        <a href={whatsappLink("Hello, I'd like one combined quote for moving and end-of-tenancy cleaning.")} onClick={() => track.whatsappClick("package-success")} target="_blank" rel="noreferrer"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-bold text-white transition-transform hover:scale-105">
          <MessageCircle size={18} /> Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form id="package-form" onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-9" data-testid="package-form" noValidate>
      <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Preferred package" testid="pf-package">
          <Select value={form.package} onValueChange={(v) => set("package", v)}>
            <SelectTrigger data-testid="pf-package-select" className={inputCls}><SelectValue placeholder="Select a package" /></SelectTrigger>
            <SelectContent>{PACKAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="hidden sm:block" />
        <Field label="Full name" required testid="pf-name"><Input data-testid="pf-name-input" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />{errors.full_name && <span className="text-xs text-red-500">{errors.full_name}</span>}</Field>
        <Field label="Phone" required testid="pf-phone"><Input data-testid="pf-phone-input" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />{errors.phone && <span className="text-xs text-red-500">{errors.phone}</span>}</Field>
        <Field label="Email" required testid="pf-email"><Input data-testid="pf-email-input" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />{errors.email && <span className="text-xs text-red-500">{errors.email}</span>}</Field>
        <Field label="Property type" testid="pf-type">
          <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
            <SelectTrigger data-testid="pf-type-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Collection address" testid="pf-collection"><Input data-testid="pf-collection" className={inputCls} value={form.collection_address} onChange={(e) => set("collection_address", e.target.value)} placeholder="Where we collect from" /></Field>
        <Field label="Delivery address" testid="pf-delivery"><Input data-testid="pf-delivery" className={inputCls} value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} placeholder="Where we move to" /></Field>
        <Field label="Number of bedrooms" testid="pf-bedrooms"><Input data-testid="pf-bedrooms" className={inputCls} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} inputMode="numeric" placeholder="e.g. 2" /></Field>
        <Field label="Number of boxes / bags" testid="pf-boxes"><Input data-testid="pf-boxes" className={inputCls} value={form.num_boxes} onChange={(e) => set("num_boxes", e.target.value)} placeholder="e.g. 15" /></Field>
        <Field label="Moving date" testid="pf-moving-date"><Input data-testid="pf-moving-date" type="date" className={inputCls} value={form.moving_date} onChange={(e) => set("moving_date", e.target.value)} /></Field>
        <Field label="Cleaning date" testid="pf-cleaning-date"><Input data-testid="pf-cleaning-date" type="date" className={inputCls} value={form.cleaning_date} onChange={(e) => set("cleaning_date", e.target.value)} /></Field>
        <Field label="Cleaning required?" testid="pf-cleaning-req">
          <Select value={form.cleaning_required} onValueChange={(v) => set("cleaning_required", v)}>
            <SelectTrigger data-testid="pf-cleaning-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{YESNO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Rubbish removal required?" testid="pf-rubbish-req">
          <Select value={form.rubbish_removal_required} onValueChange={(v) => set("rubbish_removal_required", v)}>
            <SelectTrigger data-testid="pf-rubbish-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{YESNO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Furniture assembly / disassembly required?" testid="pf-assembly-req">
          <Select value={form.furniture_assembly_required} onValueChange={(v) => set("furniture_assembly_required", v)}>
            <SelectTrigger data-testid="pf-assembly-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{YESNO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Field label="Items to move" testid="pf-items"><Textarea data-testid="pf-items" className="min-h-24 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20" value={form.items_to_move} onChange={(e) => set("items_to_move", e.target.value)} placeholder="e.g. double bed, sofa, wardrobe, washing machine" /></Field>
        <Field label="Heavy / bulky items" testid="pf-heavy"><Textarea data-testid="pf-heavy" className="min-h-24 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20" value={form.heavy_items} onChange={(e) => set("heavy_items", e.target.value)} placeholder="e.g. piano, American fridge, wardrobe" /></Field>
      </div>

      <div className="mt-5">
        <Field label="Additional information" testid="pf-message"><Textarea data-testid="pf-message" className="min-h-24 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Access, parking, floors/lift, storage needs, anything else" /></Field>
      </div>

      <div className="mt-5">
        <Label className="text-sm font-semibold text-slate-700">Upload photos (optional)</Label>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          data-testid="pf-upload-zone"
          className={`mt-3 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${dragging ? "border-emerald-600 bg-emerald-50" : "border-slate-300 bg-slate-50 hover:border-emerald-400"}`}
        >
          <Upload size={26} className="text-emerald-600" />
          <p className="mt-2 text-sm font-semibold text-slate-700">Drag & drop or click to upload</p>
          <p className="text-xs text-slate-400">Photos or video of items/rooms, up to 60MB each</p>
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="pf-file-input" onChange={(e) => addFiles(e.target.files)} />
        </div>
        {files.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {files.map((f, i) => (
              <span key={i} className="flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800">
                {f.type.startsWith("video") ? <FileVideo size={13} /> : <ImageIcon size={13} />}
                {f.name.length > 20 ? f.name.slice(0, 18) + "…" : f.name}
                <button type="button" onClick={(e) => { e.stopPropagation(); setFiles((p) => p.filter((_, idx) => idx !== i)); }} className="text-emerald-600 hover:text-red-500"><X size={13} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <label className="mt-6 flex items-start gap-3 text-sm text-slate-600">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="pf-consent" className="mt-0.5 data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700" />
        <span>I agree to be contacted regarding my combined moving and cleaning quote request.</span>
      </label>
      {errors.consent && <span className="mt-1 block text-xs text-red-500">{errors.consent}</span>}

      <button type="submit" disabled={submitting} data-testid="pf-submit" className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-700 px-8 py-4 text-base font-bold text-white transition-all hover:bg-emerald-800 disabled:opacity-60">
        {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Get One Quote for Everything"}
      </button>
    </form>
  );
};
